import { useCallback, useEffect, useState } from "react";

import { useFetch } from "Hooks/useFetch";

import Alert from "Components/Admin/Alert";
import Button from "Components/Admin/Buttons";
import ContainerWithTitle from "Components/Admin/ContainerWithTitle";
import Loading from "Components/Admin/Loading";
import PageWrapper from "Components/Admin/PageWrapper";
import Section from "Components/Admin/Section";

import * as S from "./styles";

const NotificationCenter = () => {
  const { fetchData: fetchTypes } = useFetch();
  const { fetchData: fetchRecipients, loading: loadingRecipients } = useFetch();
  const { fetchData: fetchSave, loading: loadingSave } = useFetch();

  const [messageTypes, setMessageTypes] = useState([]);
  const [activeTypeId, setActiveTypeId] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [loadingTypes, setLoadingTypes] = useState(true);

  // 1) Carrega o catálogo de tipos de mensagem e seleciona o primeiro.
  useEffect(() => {
    (async () => {
      await fetchTypes({
        method: "post",
        path: "/notification-center/list-message-types",
        body: {},
        config: { use_global_loading: false },
        onSuccessCallback: (data) => {
          const types = data?.message_types ?? [];
          setMessageTypes(types);
          if (types.length) setActiveTypeId(types[0].id);
        },
      });
      setLoadingTypes(false);
    })();
    // eslint-disable-next-line
  }, []);

  // 2) Carrega os destinatários (admins + marcação) do tipo selecionado.
  const loadRecipients = useCallback(
    async (typeId) => {
      if (!typeId) return;
      await fetchRecipients({
        method: "post",
        path: "/notification-center/list-recipients",
        body: { notification_message_type_id: typeId },
        config: { use_global_loading: false },
        onSuccessCallback: (data) => {
          const list = data?.admins ?? [];
          setAdmins(list);
          setSelectedIds(
            new Set(list.filter((a) => a.is_selected).map((a) => a.id)),
          );
        },
      });
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    loadRecipients(activeTypeId);
  }, [activeTypeId, loadRecipients]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    await fetchSave({
      method: "post",
      path: "/notification-center/save-recipients",
      body: {
        notification_message_type_id: activeTypeId,
        user_ids: [...selectedIds],
      },
      successMessage: "Destinatários atualizados com sucesso!",
      config: { show_success_message: true, use_global_loading: false },
      onSuccessCallback: () => loadRecipients(activeTypeId),
    });
  };

  const activeType = messageTypes.find((t) => t.id === activeTypeId);

  return (
    <PageWrapper>
      <Section>
        <ContainerWithTitle icon="Bell" title="Central de notificações">
          <S.Wrapper>
            {loadingTypes && <Loading />}

            {!loadingTypes && !messageTypes.length && (
              <S.EmptyMessage>
                Nenhum tipo de mensagem cadastrado.
              </S.EmptyMessage>
            )}

            {/* Abas de tipo — só aparecem quando há mais de um tipo. */}
            {messageTypes.length > 1 && (
              <S.TypeTabs>
                {messageTypes.map((type) => (
                  <S.TypeTab
                    key={type.id}
                    type="button"
                    $active={type.id === activeTypeId}
                    onClick={() => setActiveTypeId(type.id)}
                  >
                    {type.name}
                  </S.TypeTab>
                ))}
              </S.TypeTabs>
            )}

            {activeType && (
              <ContainerWithTitle title={activeType.name}>
                {loadingRecipients && <Loading />}

                {!loadingRecipients && !admins.length && (
                  <S.EmptyMessage>
                    Nenhum administrador ativo encontrado.
                  </S.EmptyMessage>
                )}

                {!loadingRecipients && admins.length > 0 && (
                  <>
                    <S.RecipientsList>
                      {admins.map((admin) => (
                        <S.RecipientRow key={admin.id}>
                          <S.Checkbox
                            checked={selectedIds.has(admin.id)}
                            onChange={() => toggle(admin.id)}
                          />
                          <S.RecipientInfo>
                            <strong>{admin.full_name}</strong>
                            <span>
                              {admin.email || "Sem e-mail cadastrado"}
                            </span>
                            {!admin.has_email && (
                              <S.AlertRow>
                                <Alert message="Usuário sem e-mail cadastrado — não receberá notificações até corrigir o cadastro." />
                              </S.AlertRow>
                            )}
                          </S.RecipientInfo>
                        </S.RecipientRow>
                      ))}
                    </S.RecipientsList>

                    <S.Footer>
                      <Button
                        text="Salvar"
                        type="button"
                        loading={loadingSave}
                        onClick={save}
                      />
                    </S.Footer>
                  </>
                )}
              </ContainerWithTitle>
            )}
          </S.Wrapper>
        </ContainerWithTitle>
      </Section>
    </PageWrapper>
  );
};

export default NotificationCenter;
